// ✅ LabourChargesEntry.jsx (FULL FILE)
// NOTE:
// 1) Added formData.labourChargeId
// 2) Added Update + Delete buttons below table
// 3) loadForUpdate() -> calls /api/getById
// 4) handleDelete() -> calls /api/delete
// 5) handleUpdateClick() -> loads 1 selected row into form
// 6) localStorage handling improved
// 7) cleaned unused values and kept UI same

import React, { useState, useContext, useEffect, useMemo } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSave,
  FaSearch,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import axios from "axios";

const BASE_URL = "http://nissi-india.com";

const LabourChargesEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const branchIdLS = getLS("branchId") || getLS("branchid") || "8";
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";

  // ✅ parsed values
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);
  const branchId = useMemo(() => Number(branchIdLS || 8), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "HO").trim(),
    [branchTypeLS]
  );

  // ✅ Dropdown Data
  const [supplierList, setSupplierList] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [labourBasisList, setLabourBasisList] = useState([]);

  const getInitialFormData = () => ({
    labourChargeId: null,
    labourChargeNo: "",
    projectCodeName: "",
    entryDate: "",
    reportedBy: "",
    isPaymentMade: false,
    malePersons: "",
    labourBasis: "",
    wageForFemale: "",
    othersQuantity: "",
    otherDeductions: "",
    totalExpenditure: "0",
    remarks: "",
    tokenNumber: "",
    siteLocation: "",
    labourSupplierName: "",
    payMode: "Cash",
    femalePersons: "",
    wageForMale: "",
    otherWorkersExpenses: "",
    othersWages: "",
    advance: "",
    paidBy: "",
    chequeNo: "",
    chequeDate: "",
    ddNo: "",
    ddDate: "",
  });

  const [formData, setFormData] = useState(getInitialFormData());

  const [showFilters, setShowFilters] = useState(true);

  const [searchData, setSearchData] = useState({
    supplier: "",
    fromDate: "",
    toDate: "",
  });

  const [labourData, setLabourData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // ✅ Fetch Dropdowns
  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/suppliers`, {
        params: { branchid: branchId, branchtype: branchType },
      });

      if (res.data.success) {
        setSupplierList(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setSupplierList([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching suppliers!");
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/projects`, {
        params: { branchid: branchId, branchtype: branchType },
      });

      if (res.data.success) {
        setProjectList(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setProjectList([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching projects!");
    }
  };

  const fetchLabourBasis = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/labourBasis`);
      if (res.data.success) {
        setLabourBasisList(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setLabourBasisList([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching labour basis!");
    }
  };

  // ✅ Fetch Labour Charges List
  const fetchLabourCharges = async () => {
    try {
      const params = {
        branchid: branchId,
        branchtype: branchType,
        supplierid: searchData.supplier || "",
        from: searchData.fromDate || "",
        to: searchData.toDate || "",
      };

      const response = await axios.get(`${BASE_URL}/api/list`, { params });

      if (response.data.success) {
        setLabourData(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setLabourData([]);
        toast.error(response.data.message || "Failed to fetch list");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching labour charges");
    }
  };

  const fetchLabourChargeNo = async () => {
    try {
      const noRes = await axios.get(`${BASE_URL}/api/nextNumber`, {
        params: { branchid: branchId, branchtype: branchType },
      });

      if (noRes.data.success) {
        handleInputChange("labourChargeNo", noRes.data.labourChargeNo || "");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching labour charge no!");
    }
  };

  // ✅ Load on mount
  useEffect(() => {
    if (!userId || !branchId) {
      toast.error("UserId or BranchId missing in localStorage!");
      return;
    }

    fetchSuppliers();
    fetchLabourChargeNo();
    fetchProjects();
    fetchLabourBasis();
    fetchLabourCharges();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearchChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ Project change -> load location
  const handleProjectChange = async (contractId) => {
    handleInputChange("projectCodeName", contractId);

    if (!contractId) {
      handleInputChange("siteLocation", "");
      return;
    }

    try {
      const locRes = await axios.get(`${BASE_URL}/api/siteLocationByProject`, {
        params: { contractid: contractId, branchid: branchId, branchtype: branchType },
      });

      if (locRes.data.success) {
        handleInputChange("siteLocation", locRes.data.data?.location || "");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching project location!");
    }
  };

  // ✅ Add new entry reset
  const handleAddNew = async () => {
    setFormData(getInitialFormData());
    setSelectedItems([]);
    await fetchLabourChargeNo();
    toast.success("Form reset for new entry!");
  };

  // ✅ Save Labour Charges (Insert / Update)
  const handleSave = async () => {
    if (
      !formData.projectCodeName ||
      !formData.entryDate ||
      !formData.tokenNumber ||
      !formData.siteLocation ||
      !formData.labourSupplierName ||
      !formData.totalExpenditure
    ) {
      toast.error("Please fill all required fields!");
      return;
    }

    const payload = {
      labourChargeId: formData.labourChargeId || null,
      labourChargeNo: formData.labourChargeNo || "",
      projectId: formData.projectCodeName || null,
      entryDate: formData.entryDate || null,
      reportedBy: formData.reportedBy || "",
      payMode: formData.payMode || "Cash",

      isPaymentMade: !!formData.isPaymentMade,
      malePersons: formData.malePersons || null,
      femalePersons: formData.femalePersons || null,

      labourBasisId: formData.labourBasis || null,

      wageForMale: formData.wageForMale || null,
      wageForFemale: formData.wageForFemale || null,

      totalExpenditure: formData.totalExpenditure || null,
      paidBy: formData.paidBy || "",
      remarks: formData.remarks || "",

      tokenNumber: formData.tokenNumber || "",
      siteLocation: formData.siteLocation || "",

      supplierId: formData.labourSupplierName || null,

      chequeDDNo:
        formData.payMode === "Cash"
          ? ""
          : formData.payMode === "Cheque"
          ? formData.chequeNo || ""
          : formData.payMode === "DD"
          ? formData.ddNo || ""
          : "",

      chequeDDDate:
        formData.payMode === "Cash"
          ? null
          : formData.payMode === "Cheque"
          ? formData.chequeDate || null
          : formData.payMode === "DD"
          ? formData.ddDate || null
          : null,

      othersNoOfPersons: formData.othersQuantity || null,
      othersPersonsWages: formData.othersWages || null,
      otherCharges: formData.otherWorkersExpenses || null,
      otherDeductions: formData.otherDeductions || null,
      advance: formData.advance || null,

      userid: userId,
      branchid: branchId,
      branchtype: branchType,
    };

    try {
      const response = await axios.post(`${BASE_URL}/api/save`, payload);

      if (response.data.success) {
        toast.success(response.data.message || "Saved!");

        if (!formData.labourChargeId && response.data.labourChargeId) {
          setFormData((prev) => ({
            ...prev,
            labourChargeId: response.data.labourChargeId,
          }));
        }

        fetchLabourCharges();
      } else {
        toast.error(response.data.message || "Save failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error saving labour charges");
    }
  };

  // ✅ Search Labour Charges
  const handleSearch = () => {
    fetchLabourCharges();
    toast.info("Searching labour charge records...");
  };

  // ✅ select row
  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ✅ Load one record into form for updating
  const loadForUpdate = async (id) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/getById`, {
        params: {
          branchid: branchId,
          branchtype: branchType,
          labourChargeId: id,
        },
      });

      if (!res.data.success) {
        toast.error(res.data.message || "Failed to load record");
        return;
      }

      const d = res.data.data || {};

      let chequeNo = "";
      let chequeDate = "";
      let ddNo = "";
      let ddDate = "";

      if (d.payMode === "Cheque") {
        chequeNo = d.chequeDDNo || "";
        chequeDate = d.chequeDDDate ? String(d.chequeDDDate).slice(0, 10) : "";
      } else if (d.payMode === "DD") {
        ddNo = d.chequeDDNo || "";
        ddDate = d.chequeDDDate ? String(d.chequeDDDate).slice(0, 10) : "";
      }

      setFormData((prev) => ({
        ...prev,
        labourChargeId: d.labourChargeId || null,
        labourChargeNo: d.labourChargeNo || "",
        projectCodeName: String(d.projectId || ""),
        entryDate: d.entryDate ? String(d.entryDate).slice(0, 10) : "",
        reportedBy: d.reportedBy || "",
        isPaymentMade: d.isPaymentMade === "Y" || d.isPaymentMade === true,
        malePersons: d.malePersons ?? "",
        femalePersons: d.femalePersons ?? "",
        labourBasis: d.labourBasisId ? String(d.labourBasisId) : "",
        wageForMale: d.wageForMale ?? "",
        wageForFemale: d.wageForFemale ?? "",
        totalExpenditure: d.totalExpenditure ?? "0",
        paidBy: d.paidBy || "",
        remarks: d.remarks || "",
        tokenNumber: d.tokenNumber || "",
        siteLocation: d.siteLocation || "",
        labourSupplierName: String(d.supplierId || ""),
        payMode: d.payMode || "Cash",
        othersQuantity: d.othersNoOfPersons ?? "",
        othersWages: d.othersPersonsWages ?? "",
        otherWorkersExpenses: d.otherCharges ?? "",
        otherDeductions: d.otherDeductions ?? "",
        advance: d.advance ?? "",
        chequeNo,
        chequeDate,
        ddNo,
        ddDate,
      }));

      toast.success("Loaded for update. Now click Save.");
    } catch (err) {
      console.error(err);
      toast.error("Error loading record");
    }
  };

  // ✅ Update button click
  const handleUpdateClick = async () => {
    if (selectedItems.length !== 1) {
      toast.error("Please select exactly 1 record to update");
      return;
    }

    await loadForUpdate(selectedItems[0]);
  };

  // ✅ Delete selected
  const handleDelete = async () => {
    if (!selectedItems.length) {
      toast.error("Please select at least one record!");
      return;
    }

    try {
      const res = await axios.post(`${BASE_URL}/api/delete`, {
        ids: selectedItems,
        branchid: branchId,
        branchtype: branchType,
        userid: userId,
      });

      if (res.data.success) {
        toast.success(res.data.message || "Deleted!");
        setSelectedItems([]);
        fetchLabourCharges();
      } else {
        toast.error(res.data.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error deleting labour charges");
    }
  };

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">LABOUR CHARGES ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Labour Charges Entry
              </h1>
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Labour charge No
                    </label>
                    <input
                      type="text"
                      value={formData.labourChargeNo}
                      onChange={(e) => handleInputChange("labourChargeNo", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                      }`}
                      readOnly
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Project Code/Name <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.projectCodeName}
                      onChange={(e) => handleProjectChange(e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                    >
                      <option value="">--Select--</option>
                      {projectList.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Entry Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.entryDate}
                      onChange={(e) => handleInputChange("entryDate", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Reported by
                    </label>
                    <input
                      type="text"
                      value={formData.reportedBy}
                      onChange={(e) => handleInputChange("reportedBy", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      id="isPaymentMade"
                      checked={formData.isPaymentMade}
                      onChange={(e) => handleInputChange("isPaymentMade", e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="isPaymentMade"
                      className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Is Payment made
                    </label>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Male (No of Persons) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.malePersons}
                      onChange={(e) => handleInputChange("malePersons", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Labour Basis
                    </label>
                    <select
                      value={formData.labourBasis}
                      onChange={(e) => handleInputChange("labourBasis", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                    >
                      <option value="">Select</option>
                      {labourBasisList.map((lb) => (
                        <option key={lb.id} value={lb.id}>
                          {lb.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Wage for Female <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.wageForFemale}
                      onChange={(e) => handleInputChange("wageForFemale", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Others Quantity
                    </label>
                    <input
                      type="number"
                      value={formData.othersQuantity}
                      onChange={(e) => handleInputChange("othersQuantity", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Other Deductions
                    </label>
                    <input
                      type="number"
                      value={formData.otherDeductions}
                      onChange={(e) => handleInputChange("otherDeductions", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Total Expenditure <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.totalExpenditure}
                      onChange={(e) => handleInputChange("totalExpenditure", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Remarks
                    </label>
                    <textarea
                      value={formData.remarks}
                      onChange={(e) => handleInputChange("remarks", e.target.value)}
                      rows={3}
                      className={`w-full px-4 py-2 border rounded-lg resize-none ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Token Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.tokenNumber}
                      onChange={(e) => handleInputChange("tokenNumber", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Site (Location) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.siteLocation}
                      onChange={(e) => handleInputChange("siteLocation", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Labour Supplier Name <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.labourSupplierName}
                      onChange={(e) => handleInputChange("labourSupplierName", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                    >
                      <option value="">Select</option>
                      {supplierList.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Pay Mode
                    </label>
                    <select
                      value={formData.payMode}
                      onChange={(e) => handleInputChange("payMode", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                    >
                      <option value="Cash">Cash</option>
                      <option value="Cheque">Cheque</option>
                      <option value="DD">DD</option>
                      <option value="Online">Online</option>
                    </select>
                  </div>

                  {formData.payMode === "Cheque" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-2">Cheque No</label>
                        <input
                          type="text"
                          value={formData.chequeNo}
                          onChange={(e) => handleInputChange("chequeNo", e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg ${
                            isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Cheque Date</label>
                        <input
                          type="date"
                          value={formData.chequeDate}
                          onChange={(e) => handleInputChange("chequeDate", e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg ${
                            isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                          }`}
                        />
                      </div>
                    </>
                  )}

                  {formData.payMode === "DD" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-2">DD No</label>
                        <input
                          type="text"
                          value={formData.ddNo}
                          onChange={(e) => handleInputChange("ddNo", e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg ${
                            isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">DD Date</label>
                        <input
                          type="date"
                          value={formData.ddDate}
                          onChange={(e) => handleInputChange("ddDate", e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg ${
                            isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                          }`}
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Female (No of Persons) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.femalePersons}
                      onChange={(e) => handleInputChange("femalePersons", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Wage for Male <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.wageForMale}
                      onChange={(e) => handleInputChange("wageForMale", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Other Workers/Expenses
                    </label>
                    <input
                      type="number"
                      value={formData.otherWorkersExpenses}
                      onChange={(e) => handleInputChange("otherWorkersExpenses", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Others wages
                    </label>
                    <input
                      type="number"
                      value={formData.othersWages}
                      onChange={(e) => handleInputChange("othersWages", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Advance
                    </label>
                    <input
                      type="number"
                      value={formData.advance}
                      onChange={(e) => handleInputChange("advance", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Paid By
                    </label>
                    <input
                      type="text"
                      value={formData.paidBy}
                      onChange={(e) => handleInputChange("paidBy", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                      }`}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-center items-center gap-6 mb-8">
                <button
                  onClick={handleAddNew}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPlus />
                  Add New
                </button>

                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg bg-gray-600 hover:bg-gray-700 text-white"
                >
                  <FaSave />
                  Save
                </button>
              </div>
            </div>
          )}

          <div
            className={`p-4 rounded-lg border mb-6 ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-blue-50 border-blue-200"
            }`}
          >
            <h2 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
              LABOUR CHARGE LISTING
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supplier
                  </label>
                  <select
                    value={searchData.supplier}
                    onChange={(e) => handleSearchChange("supplier", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                    }`}
                  >
                    <option value="">All</option>
                    {supplierList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From date
                  </label>
                  <input
                    type="date"
                    value={searchData.fromDate}
                    onChange={(e) => handleSearchChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To date
                  </label>
                  <input
                    type="date"
                    value={searchData.toDate}
                    onChange={(e) => handleSearchChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-900 border-gray-300"
                    }`}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={handleSearch}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}
              >
                <FaSearch />
                Search
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-2 py-3 text-center font-semibold border-r border-white/20">Sl.No</th>
                    <th className="px-2 py-3 text-center font-semibold border-r border-white/20">Labour Charge No</th>
                    <th className="px-2 py-3 text-center font-semibold border-r border-white/20">Token No.</th>
                    <th className="px-2 py-3 text-center font-semibold border-r border-white/20">Project Name</th>
                    <th className="px-2 py-3 text-center font-semibold border-r border-white/20">Location</th>
                    <th className="px-2 py-3 text-center font-semibold border-r border-white/20">Entry Date</th>
                    <th className="px-2 py-3 text-center font-semibold border-r border-white/20">Total Expenditure</th>
                    <th className="px-2 py-3 text-center font-semibold border-r border-white/20">Pay Mode</th>
                    <th className="px-2 py-3 text-center font-semibold border-r border-white/20">Payment Status</th>
                    <th className="px-2 py-3 text-center font-semibold border-r border-white/20">Supplier</th>
                    <th className="px-2 py-3 text-center font-semibold">Select</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {labourData.length > 0 ? (
                    labourData.map((item, index) => (
                      <tr
                        key={item.labourChargeId || index}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-2 py-2 text-center">{index + 1}</td>
                        <td className="px-2 py-2 text-center">{item.labourChargeNo}</td>
                        <td className="px-2 py-2 text-center">{item.tokenNo}</td>
                        <td className="px-2 py-2 text-center">{item.projectName}</td>
                        <td className="px-2 py-2 text-center">{item.location}</td>
                        <td className="px-2 py-2 text-center">{item.entryDate}</td>
                        <td className="px-2 py-2 text-center">{item.totalExpenditure}</td>
                        <td className="px-2 py-2 text-center">{item.payMode}</td>
                        <td className="px-2 py-2 text-center">{item.paymentStatus}</td>
                        <td className="px-2 py-2 text-center">{item.supplier}</td>
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.labourChargeId)}
                            onChange={() => handleSelectItem(item.labourChargeId)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="11"
                        className={`px-4 py-6 text-center ${
                          isDark ? "bg-gray-800 text-gray-300" : "bg-white text-gray-500"
                        }`}
                      >
                        No labour charge records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-center gap-4 mt-4">
            <button
              onClick={handleUpdateClick}
              className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg bg-blue-600 hover:bg-blue-700 text-white"
            >
              <FaSave />
              Update
            </button>

            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg bg-red-600 hover:bg-red-700 text-white"
            >
              <FaTrash />
              Delete
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default LabourChargesEntry;